/**
 * 連携中メールアカウントの表示カード.
 */

import { Button } from "components/ui/Button";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
import type { EmailConnection } from "hooks/use-email-integrations-api";
import type React from "react";
import ProviderIcon from "routes/profile/components/ProviderIcon";

const PROVIDER_LABEL: Record<string, string> = {
  gmail: "Gmail",
  outlook: "Outlook",
};

const STATUS_DISPLAY: Record<string, { label: string; tone: BadgeTone }> = {
  connected: { label: "連携済み", tone: "soft-success" },
  disconnected: { label: "再連携が必要", tone: "soft-warning" },
  error: { label: "エラー", tone: "soft-error" },
};

type Props = {
  connection: EmailConnection;
  onDisconnect: (id: string) => Promise<void> | void;
  isDisconnecting?: boolean;
};

/**
 * 連携 1 件分のカード UI.
 * @param {Props} props プロパティ
 * @return {JSX.Element} カード
 */
const EmailConnectionCard: React.FC<Props> = ({
  connection,
  onDisconnect,
  isDisconnecting,
}) => {
  const providerLabel =
    PROVIDER_LABEL[connection.provider] ?? connection.provider;
  const statusInfo = STATUS_DISPLAY[connection.status] ?? {
    label: connection.status,
    tone: "soft-info" as BadgeTone,
  };

  const lastSynced = connection.lastSyncedAt
    ? new Date(connection.lastSyncedAt).toLocaleString("ja-JP")
    : "—";

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {connection.profilePictureUrl ? (
            <img
              src={connection.profilePictureUrl}
              alt=""
              className="w-10 h-10 rounded-full border border-gray-200"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
              {providerLabel.charAt(0)}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ProviderIcon provider={connection.provider} size={18} />
              <span className="font-medium truncate">
                {connection.displayName || connection.emailAddress}
              </span>
              <span className="text-xs text-gray-400">{providerLabel}</span>
            </div>
            <div className="text-sm text-gray-600 truncate">
              {connection.emailAddress}
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
              <StatusBadge tone={statusInfo.tone}>
                {statusInfo.label}
              </StatusBadge>
              <span>最終同期: {lastSynced}</span>
            </div>
            {connection.errorMessage && (
              <div className="mt-1 text-xs text-error">
                {connection.errorMessage}
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDisconnect(connection.id)}
            disabled={isDisconnecting}
          >
            連携解除
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailConnectionCard;
